import { alpha, type PaletteColor, type SxProps } from '@mui/material';
import L2DTheme from '../Theme';

type PaletteMUIKey = 'success' | 'secondary' | 'error' | 'warning' | 'info' | 'primary';

export const DOMAIN_STATUS_PAS_SPECIFIE = 0;
export const DOMAIN_STATUS_PROSPECTION = 1;
export const DOMAIN_STATUS_ACTIF = 2;
export const DOMAIN_STATUS_REGULIER = 3;
export const DOMAIN_STATUS_ABANDONNISTE = 4;
export const DOMAIN_STATUS_PAS_INTERESSE = 5;
export const DOMAIN_STATUS_PERDU = 6;
export const DOMAIN_STATUS_AUTRE = 999;

const DOMAIN_STATUS_PALETTE: Partial<Record<number, PaletteMUIKey>> = {
    [DOMAIN_STATUS_PROSPECTION]: 'secondary',
    [DOMAIN_STATUS_ACTIF]: 'success',
    [DOMAIN_STATUS_REGULIER]: 'info',
    [DOMAIN_STATUS_ABANDONNISTE]: 'warning',
    [DOMAIN_STATUS_PERDU]: 'error',
};

const DOMAIN_STATUS_CHIP_LABELS: Partial<Record<number, (n: number) => string>> = {
    [DOMAIN_STATUS_ACTIF]: (n) => `${n} actif${n > 1 ? 's' : ''}`,
    [DOMAIN_STATUS_REGULIER]: (n) => `${n} régulier${n > 1 ? 's' : ''}`,
    [DOMAIN_STATUS_PROSPECTION]: (n) => `${n} en prospection`,
    [DOMAIN_STATUS_ABANDONNISTE]: (n) => `${n} abandonniste`,
    [DOMAIN_STATUS_PAS_INTERESSE]: (n) => `${n} pas intéressé${n > 1 ? 's' : ''}`,
    [DOMAIN_STATUS_PERDU]: (n) => `${n} perdu${n > 1 ? 's' : ''}`,
    [DOMAIN_STATUS_PAS_SPECIFIE]: (n) => `${n} non spécifié${n > 1 ? 's' : ''}`,
    [DOMAIN_STATUS_AUTRE]: (n) => `${n} autre${n > 1 ? 's' : ''}`,
};

export const DOMAIN_STATUS_CHIP_IDS = [
    DOMAIN_STATUS_ACTIF,
    DOMAIN_STATUS_REGULIER,
    DOMAIN_STATUS_PROSPECTION,
    DOMAIN_STATUS_ABANDONNISTE,
    DOMAIN_STATUS_PAS_INTERESSE,
    DOMAIN_STATUS_PERDU,
    DOMAIN_STATUS_PAS_SPECIFIE,
    DOMAIN_STATUS_AUTRE,
];

export type DomainStatusStyle = {
    paletteColor: PaletteColor | null;
    isInactive: boolean;
    dotColor: string;
    cardSx: SxProps;
    chipSx: SxProps;
};

const useDomainStatusStyle = () => {
    const { palette } = L2DTheme();

    const getDomainStatusStyle = (statusId?: number): DomainStatusStyle => {
        const paletteKey = statusId !== undefined ? DOMAIN_STATUS_PALETTE[statusId] : undefined;
        const paletteColor = paletteKey ? (palette[paletteKey] as PaletteColor) : null;

        if (!paletteColor) {
            return {
                paletteColor: null,
                isInactive: true,
                dotColor: palette.grey[400],
                cardSx: {
                    border: `1px solid ${palette.divider}`,
                    backgroundColor: palette.background.paper,
                },
                chipSx: {},
            };
        }

        return {
            paletteColor,
            isInactive: false,
            dotColor: paletteColor.main,
            cardSx: {
                border: `1px solid ${alpha(paletteColor.light, 0.5)}`,
                backgroundColor: alpha(paletteColor.light, 0.15),
            },
            chipSx: {
                backgroundColor: alpha(paletteColor.light, 0.3),
                color: paletteColor.dark,
                border: 'none',
            },
        };
    };

    const getDomainStatusLabel = (id: number, count: number): string | null =>
        DOMAIN_STATUS_CHIP_LABELS[id]?.(count) ?? null;

    return { getDomainStatusStyle, getDomainStatusLabel };
};

export default useDomainStatusStyle;
